import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Slider } from "@/components/ui/slider"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface DateFilterPanelProps {
  /** The earliest date available in the dataset (derived from year_start + month_start). */
  minDate: number
  /** The latest date available in the dataset (derived from year_end + month_end). */
  maxDate: number
  /** The currently selected start of the date range (as a year decimal, e.g. 2005.5). */
  rangeStart: number
  /** The currently selected end of the date range (as a year decimal). */
  rangeEnd: number
  /** Callback when the range start changes. */
  onRangeStartChange: (value: number) => void
  /** Callback when the range end changes. */
  onRangeEndChange: (value: number) => void
  /**
   * When true (default), the 2000 pivot is pinned to a fixed pixel offset so it
   * aligns with the PR coverage histogram beside it. On mobile the two are
   * never side by side, so callers pass false to use the plain fraction pivot.
   */
  alignPivotToPx?: boolean
}

/**
 * The year that divides the slider's two segments.
 */
const PIVOT_YEAR: number = 2000

/**
 * Fraction of the slider track (0–1) allocated to the pre-pivot segment
 * (minDate … PIVOT_YEAR). The remaining 1 - this is allocated to the post-pivot
 * segment (PIVOT_YEAR … maxDate).
 */
const PRE_PIVOT_TRACK_FRACTION: number = 0.15

/**
 * Number of steps the slider is divided into . The slider
 * operates in this integer "position" space [0, SLIDER_RESOLUTION] rather than
 * directly in year values, which is what lets the position↔year mapping be
 * non-linear. A high resolution keeps the effective month-level granularity.
 */
const SLIDER_RESOLUTION: number = 1200

/**
 * Fixed horizontal distance (px) from the slider track's left edge to the
 * pivot year (2000), used instead of PRE_PIVOT_TRACK_FRACTION so the pivot
 * lines up with the same fixed anchor in the PR coverage histogram regardless
 * of card width. The card content is inset by its own padding, so this is the
 * target 180px-from-card-left minus that padding (px-5 = 20px).
 */
const PIVOT_ANCHOR_PX_FROM_TRACK: number = 150

/**
 * Width (px) of the slider thumb (Radix renders it h-4 w-4 = 16px). Radix keeps
 * each thumb inside the track, so a value at track fraction f has its centre at
 * thumbWidth/2 + f * (trackWidth - thumbWidth), not a plain f * trackWidth. The
 * pivot tick and label must use the same geometry to sit under the thumbs.
 */
const SLIDER_THUMB_WIDTH_PX: number = 16

/**
 * Convert a year decimal (e.g. 2005.5) to a human-readable date string.
 *
 * Rounds to the nearest month first to eliminate floating-point drift that
 * accumulates from the 1/12 step size, then works in integer space to avoid
 * issues like Math.floor(2005.0) returning 2004 due to representation error.
 */
function yearDecimalToLabel(yearDecimal: number): string {
  const totalMonths = Math.round(yearDecimal * 12)
  const year = Math.floor(totalMonths / 12)
  const month = totalMonths - year * 12
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
  return `${monthNames[month]} \n ${year}`
}

const MONTH_NAMES: readonly string[] = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

interface MonthYear {
  /** Zero-based month index (0 = January … 11 = December). */
  month: number
  year: number
}

function yearDecimalToMonthYear(yearDecimal: number): MonthYear {
  const totalMonths: number = Math.round(yearDecimal * 12)
  const year: number = Math.floor(totalMonths / 12)
  const month: number = totalMonths - year * 12
  return { month, year }
}

function monthYearToYearDecimal(monthYear: MonthYear): number {
  return monthYear.year + monthYear.month / 12
}

function formatMonthYear(monthYear: MonthYear): string {
  return `${MONTH_NAMES[monthYear.month]} ${monthYear.year}`
}

export interface MonthYearPickerProps {
  label: string
  value: number
  min: number
  max: number
  onChange: (next: number) => void
}

export function MonthYearPicker({
  label,
  value,
  min,
  max,
  onChange,
}: MonthYearPickerProps): React.JSX.Element {
  const selected: MonthYear = yearDecimalToMonthYear(value)

  const [open, setOpen] = useState<boolean>(false)
  const [viewYear, setViewYear] = useState<number>(selected.year)
  // Which grid the popover shows: month buttons, or a year grid for jumping
  // across the whole range at once instead of stepping one year at a time.
  const [view, setView] = useState<"months" | "years">("months")

  const minYear: number = yearDecimalToMonthYear(min).year
  const maxYear: number = yearDecimalToMonthYear(max).year

  // Every selectable year, oldest to newest, for the year-grid view.
  const yearChoices: number[] = []
  for (let year = minYear; year <= maxYear; year++) {
    yearChoices.push(year)
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen) {
      setViewYear(selected.year)
      setView("months")
    }
    setOpen(nextOpen)
  }

  /**
   * Whether a given month in the currently-browsed year is selectable, i.e. its
   * year decimal lies within [min, max].
   */
  function isMonthEnabled(monthIndex: number): boolean {
    const candidate: number = monthYearToYearDecimal({ month: monthIndex, year: viewYear })
    return candidate >= min && candidate <= max
  }

  function handleSelectMonth(monthIndex: number): void {
    onChange(monthYearToYearDecimal({ month: monthIndex, year: viewYear }))
    setOpen(false)
  }

  function handleSelectYear(year: number): void {
    setViewYear(year)
    setView("months")
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 min-w-15 justify-center px-2 text-[11px] font-medium tabular-nums"
          aria-label={`${label} month and year`}
        >
          {formatMonthYear(selected)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="w-auto gap-3 p-3">
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            disabled={view === "years" || viewYear <= minYear}
            onClick={() => setViewYear((y) => Math.max(minYear, y - 1))}
            aria-label="Previous year"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-sm font-semibold tabular-nums"
            onClick={() => setView((current) => (current === "months" ? "years" : "months"))}
            aria-label={view === "months" ? "Choose a year" : "Back to months"}
            aria-expanded={view === "years"}
          >
            {viewYear}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6"
            disabled={view === "years" || viewYear >= maxYear}
            onClick={() => setViewYear((y) => Math.min(maxYear, y + 1))}
            aria-label="Next year"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
        </div>

        {view === "months" ? (
          <div className="grid grid-cols-3 gap-1">
            {MONTH_NAMES.map((name, index) => {
              const isSelected: boolean = index === selected.month && viewYear === selected.year
              const enabled: boolean = isMonthEnabled(index)
              return (
                <Button
                  key={name}
                  type="button"
                  variant={isSelected ? "default" : "ghost"}
                  size="sm"
                  className={cn("h-7 text-xs", !isSelected && "font-normal")}
                  disabled={!enabled}
                  onClick={() => handleSelectMonth(index)}
                >
                  {name}
                </Button>
              )
            })}
          </div>
        ) : (
          <div className="grid max-h-48 grid-cols-4 gap-1 overflow-y-auto">
            {yearChoices.map((year) => {
              const isSelected: boolean = year === selected.year
              return (
                <Button
                  key={year}
                  type="button"
                  variant={isSelected ? "default" : "ghost"}
                  size="sm"
                  className={cn("h-7 text-xs tabular-nums", !isSelected && "font-normal")}
                  onClick={() => handleSelectYear(year)}
                >
                  {year}
                </Button>
              )
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Linearly interpolate / extrapolate within a single segment.
 *
 * @param value - The input value to remap.
 * @param inMin - Start of the input range.
 * @param inMax - End of the input range.
 * @param outMin - Start of the output range.
 * @param outMax - End of the output range.
 * @returns The value remapped from [inMin, inMax] onto [outMin, outMax]. When
 *   inMin === inMax the input range is degenerate, so outMin is returned to
 *   avoid a divide-by-zero.
 */
function remapSegment(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  if (inMax === inMin) {
    return outMin
  }
  const t: number = (value - inMin) / (inMax - inMin)
  return outMin + t * (outMax - outMin)
}

export default function DateFilterPanel({
  minDate,
  maxDate,
  rangeStart,
  rangeEnd,
  onRangeStartChange,
  onRangeEndChange,
  alignPivotToPx = true,
}: DateFilterPanelProps): React.JSX.Element {
  /**
   * The pivot year clamped into the actual data range. When the dataset starts
   * after the pivot, there is no pre-pivot segment, so the pivot collapses to minDate
   * and the whole track becomes linear; likewise if the data ends before the pivot.
   */
  const effectivePivot: number = useMemo((): number => {
    return Math.min(Math.max(PIVOT_YEAR, minDate), maxDate)
  }, [minDate, maxDate])

  // Rendered width (px) of the slider track, tracked so the pivot can be pinned
  // to a fixed pixel offset regardless of card width. Null until first measured.
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [trackWidth, setTrackWidth] = useState<number | null>(null)

  useEffect(() => {
    const element: HTMLDivElement | null = trackRef.current
    if (element == null) {
      return
    }
    const observer: ResizeObserver = new ResizeObserver((entries) => {
      const entry: ResizeObserverEntry | undefined = entries[0]
      if (entry != null) {
        // Round + only commit on change so the pivot recompute doesn't
        // retrigger the observer on sub-pixel jitter.
        const nextWidth: number = Math.round(entry.contentRect.width)
        setTrackWidth((prev) => (prev === nextWidth ? prev : nextWidth))
      }
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  /**
   * The slider position (in [0, SLIDER_RESOLUTION]) at which the two segments meet.
   */
  const pivotPosition: number = useMemo((): number => {
    if (effectivePivot <= minDate) {
      return 0
    }
    if (effectivePivot >= maxDate) {
      return SLIDER_RESOLUTION
    }
    // Pin the pivot (2000) a fixed number of pixels from the track's left edge
    // rather than a fixed fraction, so it always lands at the same card-x as
    // the histogram's 2000 column. Position space maps linearly to track pixels
    // (0..SLIDER_RESOLUTION over 0..trackWidth), so the fraction is the anchor
    // pixel over the measured width. Until the width is measured, fall back to
    // PRE_PIVOT_TRACK_FRACTION so the first paint is still reasonable.
    // Use the plain fraction when alignment is off (mobile) or the track is too
    // narrow: below this width the PR coverage histogram beside it drops its
    // 2000-alignment gutter and goes full width, so there is nothing to pin to.
    // The threshold mirrors the coverage card's 500px narrow-layout switch,
    // minus the card's horizontal padding (~px-5 each side).
    const NARROW_TRACK_PX: number = 460
    if (
      !alignPivotToPx ||
      trackWidth == null ||
      trackWidth <= 0 ||
      trackWidth < NARROW_TRACK_PX
    ) {
      return PRE_PIVOT_TRACK_FRACTION * SLIDER_RESOLUTION
    }
    const fraction: number = PIVOT_ANCHOR_PX_FROM_TRACK / trackWidth
    const clampedFraction: number = Math.min(Math.max(fraction, 0), 1)
    return clampedFraction * SLIDER_RESOLUTION
  }, [effectivePivot, minDate, maxDate, trackWidth, alignPivotToPx])

  /**
   * Convert a year decimal to its slider position using the piecewise-linear
   * warp: [minDate, pivot] maps to [0, pivotPosition] and [pivot, maxDate] maps
   * to [pivotPosition, SLIDER_RESOLUTION].
   */
  const yearToPosition = useCallback(
    (year: number): number => {
      if (year <= effectivePivot) {
        return remapSegment(year, minDate, effectivePivot, 0, pivotPosition)
      }
      return remapSegment(year, effectivePivot, maxDate, pivotPosition, SLIDER_RESOLUTION)
    },
    [minDate, maxDate, effectivePivot, pivotPosition]
  )

  /**
   * Inverse of {@link yearToPosition}: convert a slider position back to a year
   * decimal, choosing the segment the position falls in.
   */
  const positionToYear = useCallback(
    (position: number): number => {
      if (position <= pivotPosition) {
        return remapSegment(position, 0, pivotPosition, minDate, effectivePivot)
      }
      return remapSegment(position, pivotPosition, SLIDER_RESOLUTION, effectivePivot, maxDate)
    },
    [minDate, maxDate, effectivePivot, pivotPosition]
  )

  /**
   * Handle value changes from the dual-thumb slider. The slider operates in
   * position space, so each thumb position is converted back to a year decimal
   * before the callbacks fire.
   */
  function handleSliderChange(values: number[]): void {
    const [startPos, endPos] = values
    const newStart: number = positionToYear(startPos)
    const newEnd: number = positionToYear(endPos)
    if (newStart !== rangeStart) {
      onRangeStartChange(newStart)
    }
    if (newEnd !== rangeEnd) {
      onRangeEndChange(newEnd)
    }
  }

  const showPivotTick: boolean = pivotPosition > 0 && pivotPosition < SLIDER_RESOLUTION

  // Pixel x of the pivot (2000), matching Radix's thumb placement so the tick
  // and label sit exactly under the thumb when a thumb is at 2000. Radix insets
  // each thumb by half its width at both ends, so a value at track fraction f
  // lands at thumbWidth/2 + f * (trackWidth - thumbWidth). Null until the track
  // width is measured; falls back to a percentage position for the first paint.
  const pivotFraction: number = pivotPosition / SLIDER_RESOLUTION
  const pivotLeftPx: number | null =
    trackWidth == null || trackWidth <= 0
      ? null
      : SLIDER_THUMB_WIDTH_PX / 2 +
        pivotFraction * (trackWidth - SLIDER_THUMB_WIDTH_PX)

  // CSS left for the tick/label: the thumb-matched pixel when measured, else a
  // plain percentage for the brief pre-measurement paint.
  const pivotLeftCss: string =
    pivotLeftPx == null ? `${pivotFraction * 100}%` : `${pivotLeftPx}px`

  return (
    <div>
      <div ref={trackRef} className="relative">
        <Slider
          min={0}
          max={SLIDER_RESOLUTION}
          step={1}
          value={[yearToPosition(rangeStart), yearToPosition(rangeEnd)]}
          onValueChange={handleSliderChange}
          aria-label="Date range slider"
          className="[&_[data-slot=slider-track]]:bg-gray-200 [&_[data-slot=slider-thumb]]:border-sidebar-ring [&_[data-slot=slider-thumb]]:bg-sidebar-ring
          "
        />

        {showPivotTick && (
          <div
            className="pointer-events-none absolute top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2 bg-muted-foreground/50"
            style={{ left: pivotLeftCss }}
            aria-hidden="true"
          />
        )}
      </div>

      <div className="relative mt-3 text-[10px] whitespace-pre-line leading-2.5 text-center text-ring">
        <span className="absolute left-0">{yearDecimalToLabel(minDate)}</span>
        {showPivotTick && (
          <span className="absolute -translate-x-1/2" style={{ left: pivotLeftCss }}>
            {yearDecimalToLabel(effectivePivot)}
          </span>
        )}
        <span className="absolute right-0">{yearDecimalToLabel(maxDate)}</span>
        &nbsp;
      </div>
    </div>
  )
}
